<template>
  <modal-wrap
    :id="id"
    ref="listUsers"
    body-class="users-modal"
    :name="modalName"
    :header-class="'users-header'"
    :hide-footer="true"
    size="md"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0"><mdicon class="pointer" name="account-group" /> Users</h4>
      </div>
    </template>
    <template #default>
      <button class="btn btn-green" @click="openUserCreateModal">
        <mdicon name="plus" size="14" class="dropdown-green" />
        Create New
      </button>
      <div class="element-blocks">
        <div
          v-for="user in users"
          :id="'element' + user?.id"
          :key="user?.id || 'user'"
          :ref="'element' + user?.id"
          class="element"
          @mouseover="showActionButtons(user?.id)"
          @mouseleave="hideActionButtons(user?.id)"
        >
          <div class="element-actions">
            <div class="row w-100 m-auto">
              <div class="col-2">
                <b-button
                  class="d-flex align-items-center justify-content-center w-100 action-btn"
                  variant="outline-secondary"
                  @click="openUserEditModal(user)"
                >
                  <mdicon name="pencil-outline" class="pointer" size="24" />
                </b-button>
              </div>
              <div class="col-2">
                <b-button
                  class="d-flex align-items-center justify-content-center w-100 action-btn"
                  variant="outline-secondary"
                  @click="destroyUser(user.id)"
                >
                  <mdicon name="delete-outline" class="pointer" size="24" />
                </b-button>
              </div>
              <div v-if="isStaff" class="col-2">
                <b-button
                  class="d-flex align-items-center justify-content-center w-100 action-btn"
                  variant="outline-secondary"
                  @click="authAsUser(user)"
                >
                  <mdicon name="account-arrow-left" class="pointer" size="24" />
                </b-button>
              </div>
            </div>
          </div>
          <div class="user-name d-flex align-items-center">
            <span>{{ user.email }}</span>
            <b-badge v-if="user.role" :variant="roleBadgeVariant(user.role)" class="ml-2">
              {{ roleLabel(user.role) }}
            </b-badge>
          </div>
        </div>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { Account } from '@/types/Models/Account'
import type { UsersModal } from '@/types/Models/User'
import { BButton, BBadge } from 'bootstrap-vue-next'
import { defineComponent, type ComputedRef, computed, watch } from 'vue'
import { USERS, USERS_CREATE_EDIT } from '@/const/ModalTypes'
import { getRoleBadgeVariant, getRoleLabel, none } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAuthStore } from '@/store/auth'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'UsersModal',
  components: { BButton, BBadge, ModalWrap },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const modalStore = useVueNextModalStore()
    const accountStore = useAccountStore()
    const authStore = useAuthStore()
    const modalName = USERS

    const modalData: ComputedRef<UsersModal> = computed(() => modalStore.modalPropsByName(modalName) || {})
    const companyId: ComputedRef<string | null> = computed(() => modalData.value?.companyId || null)
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)

    const users: ComputedRef<Account[]> = computed(() => accountStore.companyUsers || [])

    const fetchUsers = () => {
      if (!companyId.value) return
      accountStore.fetchCompanyUsers(companyId.value)
    }

    const openUserCreateModal = () => {
      openModal(USERS_CREATE_EDIT, { companyId: companyId.value })
    }
    const openUserEditModal = (user: Account) => {
      openModal(USERS_CREATE_EDIT, { user, companyId: companyId.value })
    }
    const authAsUser = (user: Account) => {
      if (!companyId.value || !user.id) return
      authStore.authAsCompany(companyId.value, user.id).catch(none)
    }
    const destroyUser = (userId?: string | null) => {
      if (!userId) return
      openModal('confirmWrap', {
        message: 'The user account will be deleted.',
        title: 'Are you sure you want to delete user?',
        okButtonText: 'Delete',
        onConfirm: () => {
          accountStore.destroy(userId).then(fetchUsers).catch(fetchUsers)
        },
      })
    }

    const showActionButtons = (userId?: string | null) => {
      if (!userId) return
      document.getElementById(`element${userId}`)?.classList.add('active')
    }

    const hideActionButtons = (userId?: string | null) => {
      if (!userId) return
      document.getElementById(`element${userId}`)?.classList.remove('active')
    }

    watch(companyId, fetchUsers)

    return {
      users,
      isStaff,
      modalName,
      modalData,
      roleLabel: getRoleLabel,
      roleBadgeVariant: getRoleBadgeVariant,
      showActionButtons,
      hideActionButtons,
      openUserCreateModal,
      openUserEditModal,
      authAsUser,
      destroyUser,
    }
  },
})
</script>
